import React, { useState } from 'react';
import { eventsService } from '../../services/eventsService';
import './MovePositionWidget.css';

const emptyForm = { eventReference: '', newDate: '' };

const CorrectEventDateWidget = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const payload = () => ({
    event_reference: form.eventReference.trim(),
    new_date: form.newDate,
  });

  const open = () => {
    setError('');
    setPreview(null);
    setIsOpen(true);
  };

  const close = () => {
    if (saving) return;
    setIsOpen(false);
    setPreview(null);
    setError('');
  };

  const handlePreview = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setPreview(null);
    if (!form.eventReference.trim() || !form.newDate) {
      setError('Enter an event reference and the corrected date.');
      return;
    }

    setSaving(true);
    const result = await eventsService.previewEventDateCorrection(payload());
    setSaving(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    setPreview(result);
  };

  const confirm = async () => {
    setSaving(true);
    setError('');
    const result = await eventsService.correctEventDate(payload());
    setSaving(false);
    if (!result.success) {
      setError(result.error);
      setPreview(null);
      return;
    }

    setMessage(
      `${result.message} Updated ${result.positions_count} position(s), `
      + `${result.purchases_count} purchase(s), and ${result.orders_count} order(s).`,
    );
    setForm(emptyForm);
    setPreview(null);
    setIsOpen(false);
  };

  return (
    <>
      <section className={`correct-event-date-launch ${className}`.trim()}>
        <div>
          <h3>Event Date Correction</h3>
          <p>Correct an event date and synchronize every linked position, purchase, and order.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={open}>
          Correct Event Date
        </button>
        {message && (
          <div className="move-position-widget-alert move-position-widget-alert-success">
            {message}
          </div>
        )}
      </section>

      {isOpen && (
        <div className="move-position-preview-backdrop" onMouseDown={close}>
          <section
            className="move-position-preview-modal correct-event-date-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="move-position-preview-header">
              <div>
                <h2>Correct Event Date</h2>
                <p>The correction is applied to the entire event reference.</p>
              </div>
              <button
                className="move-position-preview-close"
                type="button"
                onClick={close}
                disabled={saving}
                aria-label="Close event date correction"
              >
                x
              </button>
            </div>

            <form className="correct-event-date-form" onSubmit={handlePreview}>
              <label>
                <span>Event</span>
                <input
                  type="text"
                  value={form.eventReference}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    eventReference: event.target.value,
                  }))}
                  placeholder="Event ref, source UUID, or event ID"
                  disabled={saving || Boolean(preview)}
                />
              </label>
              <label>
                <span>Correct Date</span>
                <input
                  type="date"
                  value={form.newDate}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    newDate: event.target.value,
                  }))}
                  disabled={saving || Boolean(preview)}
                />
              </label>

              {error && (
                <div className="move-position-widget-alert move-position-widget-alert-error">
                  {error}
                </div>
              )}

              {!preview && (
                <div className="move-position-preview-actions correct-event-date-actions">
                  <button className="btn btn-secondary" type="button" onClick={close} disabled={saving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={saving}>
                    {saving ? 'Checking...' : 'Preview Correction'}
                  </button>
                </div>
              )}
            </form>

            {preview && (
              <>
                <div className="correct-event-date-preview">
                  <h3>{preview.event_name}</h3>
                  <p>{preview.venue || 'No venue'} · Event ref {preview.event_ref}</p>
                  <div className="correct-event-date-change">
                    <span>{preview.current_dates?.join(', ') || 'No current date'}</span>
                    <strong>→</strong>
                    <span>{preview.new_date}</span>
                  </div>
                  <div className="correct-event-date-impact">
                    <span>{preview.positions_count} position(s)</span>
                    <span>{preview.purchases_count} purchase(s)</span>
                    <span>{preview.orders_count} order(s)</span>
                  </div>
                  {preview.order_ids?.length > 0 && (
                    <p>Orders: {preview.order_ids.join(', ')}</p>
                  )}
                  {preview.purchase_ids?.length > 0 && (
                    <p>Purchases: {preview.purchase_ids.join(', ')}</p>
                  )}
                </div>
                <div className="move-position-preview-actions">
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => setPreview(null)}
                    disabled={saving}
                  >
                    Back
                  </button>
                  <button className="btn btn-primary" type="button" onClick={confirm} disabled={saving}>
                    {saving ? 'Updating...' : 'Confirm Date Correction'}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default CorrectEventDateWidget;
